import { useState } from "react";
import { Flame, Loader2 } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import api from "../lib/api";

// Escasez real: plazas semanales por plan (backend impone el tope de verdad, ver
// WEEKLY_PLAN_SEATS/seats_state en server.py). Muestra el contador cuando quedan plazas, o una
// captura de email para la lista de espera cuando se agotan.
//
// Compartido entre Landing.jsx (visitante anónimo, pide el email) y las superficies para usuarios
// YA logueados (UpgradeModal, Paywall) vía `email` — si se pasa, se usa ese en vez de pedirlo,
// porque volver a preguntar el correo a quien ya ha iniciado sesión es fricción de más.
export default function SeatBadge({ plan, info, weekEndLabel, email: fixedEmail }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!info) return null;

  const join = async () => {
    const target = fixedEmail || email;
    if (!target) return;
    setBusy(true);
    try {
      await api.post("/plan-seats/waitlist", { email: target, plan });
      setJoined(true);
    } catch { toast.error(t("landing.waitlistError")); }
    setBusy(false);
  };

  if (info.remaining > 0) {
    return (
      <div data-testid={`seats-left-${plan}`} className="mt-4 flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <Flame size={16} className="shrink-0" />
        <span>
          <Trans i18nKey="landing.seatsLeft" count={info.remaining} values={{ date: weekEndLabel }}
            components={{ 1: <strong className="font-bold" /> }} />
        </span>
      </div>
    );
  }
  return (
    <div data-testid={`seats-full-${plan}`} className="mt-4 space-y-2">
      <div className="flex items-center gap-1.5 text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        <Flame size={16} className="shrink-0" /> {t("landing.seatsFull")}
      </div>
      {joined ? (
        <p className="text-sm text-emerald-700 font-semibold">✅ {t("auth.waitlistJoined")}</p>
      ) : fixedEmail ? (
        <button type="button" onClick={join} disabled={busy} data-testid={`waitlist-join-${plan}`}
          className="w-full text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg px-3 py-2 disabled:opacity-60 flex items-center justify-center gap-1">
          {busy && <Loader2 size={14} className="animate-spin" />} {t("auth.joinWaitlist")}
        </button>
      ) : (
        // Columna, no fila: el botón tiene texto largo ("Unirme a la lista de espera") y no se
        // encoge (shrink-0) — en una tarjeta de precio estrecha eso dejaba al input sin apenas
        // ancho, reducido a un cuadradito casi invisible (bug real reportado ago-2026).
        <div className="flex flex-col gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")}
            data-testid={`waitlist-email-${plan}`}
            className="w-full border-2 border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-blue-600 outline-none" />
          <button type="button" onClick={join} disabled={busy || !email} data-testid={`waitlist-join-${plan}`}
            className="w-full text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg px-3 py-2 disabled:opacity-60 flex items-center justify-center gap-1">
            {busy && <Loader2 size={14} className="animate-spin" />} {t("auth.joinWaitlist")}
          </button>
        </div>
      )}
    </div>
  );
}

// Etiqueta de fecha "hasta el N de mes" en el idioma activo — misma función en los 3 sitios que
// consumen SeatBadge, así que vive aquí en vez de triplicarse.
export function fmtWeekEnd(iso, lang) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-ES", { day: "numeric", month: "long" }); }
  catch { return ""; }
}
